import type { ReactNode } from 'react'
import type { ReceiptItem } from '@domain/receipt'
import type { ReceiptItemPatch } from '@domain/ports/receipt-repository'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { IconeCheque, IconeLixeira, IconePular, IconeTrocar, IconeX } from '@ui/shell/icones'

/**
 * O "⋯" de uma linha da nota: o que não é gesto de toda linha.
 *
 * ## Por que estas três ficam aqui, e aceitar e editar ficam à vista
 *
 * Eram cinco links sublinhados em cada linha, cada um repetindo o texto cru
 * ("Trocar produto de AGUA CRISTAL 510ML", "Pular AGUA CRISTAL 510ML"…), e
 * numa nota de 41 itens isso eram 200 links. Aceitar a sugestão e corrigir a
 * leitura são o trabalho de quase toda linha; trocar o produto, pular e dizer
 * que a linha não existe são exceção. A escolha foi do dono do projeto.
 *
 * ## Folha, e não menu flutuante
 *
 * A lista mora dentro de um vidro com `backdrop-filter`, e isso faz de cada
 * cartão a referência de qualquer `fixed` lá dentro (decisão 242e): o fundo que
 * fecha um menu ancorado cobriria só o cartão, e o cartão do fechamento, logo
 * abaixo, pintaria por cima do menu da última linha. A folha é renderizada
 * pela tela, fora do vidro — o mesmo arranjo do menu de item dos lembretes.
 *
 * ## Cada ação diz o que faz com o dinheiro
 *
 * As duas parecem a mesma coisa e são opostas na conta: uma linha deixada sem
 * produto continua somando, a tirada da conta sai. Por isso o próprio VERBO
 * diz a consequência — "Deixar sem produto", "Tirar da soma" — e não o nome
 * interno do estado. Até a decisão 306 eram "Pular" e "Não existe", e
 * "pular" soava como tirar: o oposto do que fazia. Na 309, "Tirar da conta"
 * virou "Tirar da soma": no app, "conta" também é conta bancária.
 *
 * ## A linha MANUAL tem uma ação só: apagar
 *
 * Trocar produto, pular e "não existe" são respostas a uma leitura — e não
 * houve leitura nenhuma. Apagar é o gesto certo justamente porque não há
 * evidência a preservar: um frete digitado por engano não precisa virar um
 * fantasma riscado na lista. Ver `ReceiptItem.manual`.
 */
export function AcoesDaLinha({
  linha,
  nome,
  onTrocar,
  onDecidir,
  onApagar,
  onFechar,
  divergencia = null,
}: {
  linha: ReceiptItem
  /** Como a linha aparece na lista — ver `LinhaDaNota`. */
  nome: string
  /**
   * As duas medidas, quando o cupom e o produto sugerido discordam — e aí a
   * folha vira a de CONFERIR (decisão 308): a frase no topo e "Aceitar mesmo
   * assim" como primeira saída, ao lado das outras duas.
   */
  divergencia?: string | null
  onTrocar: () => void
  onDecidir: (patch: ReceiptItemPatch) => void
  /** Só a linha manual é apagável. Ver o cabeçalho. */
  onApagar: () => void
  onFechar: () => void
}) {
  const decidir = (patch: ReceiptItemPatch) => {
    onDecidir(patch)
    onFechar()
  }

  return (
    <FolhaDeBaixo titulo={`Ações de ${linha.rawText}`} onFechar={onFechar}>
      {/* Sem produto, o nome da lista É o texto cru: a folha o dizia duas
          vezes, uma em cima da outra (decisão 311). */}
      {nome !== linha.rawText && <p className="truncate px-1 text-sm font-medium">{nome}</p>}
      <p className="mb-2 truncate px-1 font-mono text-xs text-[var(--text-muted)]">
        {linha.rawText}
      </p>
      {divergencia !== null && (
        <p className="mb-2 px-1 text-sm" style={{ color: 'var(--pendente)' }}>
          {divergencia}
        </p>
      )}

      <div className="flex flex-col">
        {linha.manual ? (
          <Acao
            icone={<IconeLixeira />}
            explicacao="Ela sai da nota. Não há leitura para guardar."
            onClick={() => {
              onApagar()
              onFechar()
            }}
          >
            Apagar
          </Acao>
        ) : (
          <>
            {divergencia !== null && linha.matchStatus === 'pending' && (
              // Primeira, e não escondida: pode ser o CADASTRO que está
              // errado, e quem tem o papel na mão decide. O que mudou é que
              // agora é uma escolha entre três, com as medidas à vista.
              <Acao
                icone={<IconeCheque />}
                explicacao="A diferença é do cadastro, e o produto é este mesmo."
                onClick={() => {
                  decidir({ matchStatus: 'confirmed' })
                }}
              >
                Aceitar mesmo assim
              </Acao>
            )}
            <Acao
              icone={<IconeTrocar />}
              explicacao="Escolher no catálogo. O vínculo fica aprendido."
              onClick={onTrocar}
            >
              Trocar produto
            </Acao>
            {linha.matchStatus === 'confirmed' && linha.productId !== null && (
              // Aceitar era de mão única: o "⋯" de uma linha aceita não
              // oferecia volta (crítica de 2026-10-03).
              <Acao
                icone={null}
                explicacao="O produto volta a ser só uma sugestão, esperando você conferir."
                onClick={() => {
                  decidir({ matchStatus: 'pending' })
                }}
              >
                Voltar a sugerido
              </Acao>
            )}
            {linha.matchStatus !== 'skipped' && (
              <Acao
                icone={<IconePular />}
                explicacao="Não vira produto, e continua na soma."
                onClick={() => {
                  decidir({ matchStatus: 'skipped' })
                }}
              >
                Deixar sem produto
              </Acao>
            )}
            {linha.matchStatus !== 'rejected' && (
              <Acao
                icone={<IconeX />}
                explicacao="A leitura inventou esta linha: ela sai da soma."
                onClick={() => {
                  decidir({ matchStatus: 'rejected' })
                }}
              >
                Tirar da soma
              </Acao>
            )}
            {/* Sem isto, pular por engano só se desfazia escolhendo um produto — e
            a linha da sacola não tem produto para escolher. */}
            {(linha.matchStatus === 'skipped' || linha.matchStatus === 'rejected') && (
              <Acao
                icone={null}
                explicacao="Volta a esperar decisão, e a entrar na soma."
                onClick={() => {
                  decidir({ matchStatus: 'pending' })
                }}
              >
                Desfazer
              </Acao>
            )}
          </>
        )}

        <button
          type="button"
          onClick={onFechar}
          className="alvo mt-1 justify-start rounded-lg border-t border-[var(--border)] px-3 text-left text-sm text-[var(--text-muted)]"
        >
          Fechar
        </button>
      </div>
    </FolhaDeBaixo>
  )
}

function Acao({
  icone,
  explicacao,
  onClick,
  children,
}: {
  icone: ReactNode
  explicacao: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="alvo justify-start gap-3 rounded-lg px-3 py-2 text-left text-sm"
    >
      <span className="grid size-6 shrink-0 place-items-center text-[var(--text-muted)]">
        {icone}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block">{children}</span>
        <span className="block text-xs text-[var(--text-muted)]">{explicacao}</span>
      </span>
    </button>
  )
}
